import React, { useState, useEffect } from 'react';
import { Card, Select, Space, Typography, Row, Col, Progress, Button, Checkbox, Statistic, List, Tag, Alert, Divider, Badge } from 'antd';
import { LeftOutlined, RightOutlined, InfoCircleOutlined, BulbOutlined, ClockCircleOutlined, ThunderboltOutlined, UserOutlined, EnvironmentOutlined, ExclamationCircleOutlined, WarningOutlined } from '@ant-design/icons';
import dayjs from '../../../dayjs';
import otrDataProcessor from '../utils/OTRDataProcessor';

// Add import for OTR_THRESHOLDS from OTRDashboard
const OTR_THRESHOLDS = {
  EARLY_MINUTES: -2,   // Early threshold in minutes (negative value)
  LATE_MINUTES: 5      // Late threshold in minutes (positive value)
};

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const CheckboxGroup = Checkbox.Group;

/**
 * Radial chart to display On-Time Running performance by date
 * Shows a circular progress bar with:
 * - Green color when performance is ≥95% (meets target)
 * - Red color when performance is <95% (below target)
 */
const OTRPerformanceRadialChart = ({ data, title = "Route Performance" }) => {
  const [availableRoutes, setAvailableRoutes] = useState([]);
  const [selectedRoute, setSelectedRoute] = useState('all');
  const [currentMonth, setCurrentMonth] = useState(dayjs());
  const [visibleDates, setVisibleDates] = useState([]);
  const [datePerformance, setDatePerformance] = useState({});
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [selectedStopTypes, setSelectedStopTypes] = useState(['first']);

  // Calculate overall performance based on selected stop types
  const calculateOverallPerformance = (date) => {
    const dateKey = date.format('YYYYMMDD');
    const dateData = datePerformance[dateKey];
    
    if (!dateData) return { percent: 0, total: 0 };
    
    // Log for debugging the data
    console.log(`[${date.format('YYYY-MM-DD')}] Using OTR_THRESHOLDS - Early: ${OTR_THRESHOLDS.EARLY_MINUTES}, Late: ${OTR_THRESHOLDS.LATE_MINUTES}. Overall percent: ${dateData.percent}%, total: ${dateData.total}, selectedStopTypes: ${selectedStopTypes.join(',')}, dateKey: ${dateKey}`);
    
    // If 'ALL' is selected, use the pre-calculated values from the aggregated data
    // which now reflects the filtered data from the parent component
    if (selectedStopTypes.includes('ALL')) {
      return dateData;
    }
    
    // Otherwise calculate performance only for selected stop types
    return calculateFilteredPerformance(date);
  };

  // Calculate filtered performance based on selected stop types
  const calculateFilteredPerformance = (date) => {
    const dateKey = date.format('YYYYMMDD');
    const dateData = datePerformance[dateKey];
    
    if (!dateData) return { percent: 0, total: 0 };

    // If no specific stop types are selected, return zero
    if (selectedStopTypes.length === 0) {
      return { percent: 0, total: 0 };
    }
    
    // Sum up the data for selected stop types
    let onTime = 0;
    let total = 0;
    
    if (selectedStopTypes.includes('F') && dateData.firstStops) {
      onTime += dateData.firstStops.onTime;
      total += dateData.firstStops.total;
    }
    
    if (selectedStopTypes.includes('M') && dateData.midStops) {
      onTime += dateData.midStops.onTime;
      total += dateData.midStops.total;
    }
    
    if (selectedStopTypes.includes('L') && dateData.lastStops) {
      onTime += dateData.lastStops.onTime;
      total += dateData.lastStops.total;
    }
    
    if (total === 0) return { percent: 0, total: 0 };
    
    return {
      percent: Math.round((onTime / total) * 100),
      total
    };
  };

  // Generate dates for the current view (2 weeks)
  useEffect(() => {
    const dates = [];
    const startDate = currentMonth.startOf('month');
    const daysInMonth = currentMonth.daysInMonth();
    
    for (let i = 0; i < daysInMonth; i++) {
      dates.push(startDate.add(i, 'day'));
    }
    
    setVisibleDates(dates);
  }, [currentMonth]);

  // Extract routes from data and prepare performance data
  useEffect(() => {
    if (data && data.length > 0) {
      // Extract unique routes
      const routes = [...new Set(data.map(item => item.ROUTE))]
        .filter(Boolean)
        .sort((a, b) => {
          if (typeof a === 'number' && typeof b === 'number') {
            return a - b;
          }
          return String(a).localeCompare(String(b));
        });
      setAvailableRoutes(routes);
      
      // Calculate performance for each date using centralized processor
      calculatePerformanceByDate(data, selectedRoute);
    }
  }, [data]);
  
  // Recalculate when route selection changes
  useEffect(() => {
    if (data && data.length > 0) {
      calculatePerformanceByDate(data, selectedRoute);
    }
  }, [selectedRoute]);

  const calculatePerformanceByDate = (rawData, routeFilter) => {
    if (!rawData || rawData.length === 0) {
      setDatePerformance({});
      return;
    }
    
    // Log data count for debugging
    console.log(`Processing ${rawData.length} records`);
    
    // Filter by selected route if not 'all'
    const filteredByRoute = routeFilter !== 'all'
      ? rawData.filter(item => String(item.ROUTE) === String(routeFilter))
      : rawData;
    
    // Use centralized processor for daily performance calculation
    const dailyPerformance = otrDataProcessor.calculateDailyPerformance(filteredByRoute, ['ALL'], false);
    
    // Convert the date keys to the format expected by this component
    const convertedPerformance = {};
    Object.entries(dailyPerformance).forEach(([dateStr, perfData]) => {
      // Convert YYYYMMDD to YYYY-MM-DD format for consistency
      let formattedDateStr = dateStr;
      if (dateStr.length === 8 && !dateStr.includes('-')) {
        const year = dateStr.substring(0, 4);
        const month = dateStr.substring(4, 6);
        const day = dateStr.substring(6, 8);
        formattedDateStr = `${year}-${month}-${day}`;
            }
      
      convertedPerformance[formattedDateStr] = {
        onTime: perfData.onTimeCount || 0,
        total: perfData.totalCount || 0,
        percent: perfData.onTimePercent || 0,
        firstStops: {
          onTime: perfData.firstStops?.onTime || 0,
          total: perfData.firstStops?.total || 0
        },
        midStops: {
          onTime: perfData.midStops?.onTime || 0,
          total: perfData.midStops?.total || 0,
          hasMidpoints: (perfData.midStops?.total || 0) > 0
        },
        lastStops: {
          onTime: perfData.lastStops?.onTime || 0,
          total: perfData.lastStops?.total || 0
        }
      };
    });
    
    setDatePerformance(convertedPerformance);
  };
  
  // Navigate to previous month
  const handlePrevMonth = () => {
    setCurrentMonth(currentMonth.subtract(1, 'month'));
  };
  
  // Navigate to next month
  const handleNextMonth = () => {
    setCurrentMonth(currentMonth.add(1, 'month'));
  };
  
  // Handle route selection change
  const handleRouteChange = (value) => {
    setSelectedRoute(value);
  };
  
  // Handle date selection
  const handleDateSelect = (date) => {
    setSelectedDate(date);
  };

  // Handle stop type selection change
  const handleStopTypeChange = (checkedValues) => {
    // If we're trying to uncheck the last option, don't do it
    if (checkedValues.length === 0) {
      return;
    }
    
    setSelectedStopTypes(checkedValues);
  };
  
  // Format date for display (e.g. "Fri 9 May")
  const formatDateDisplay = (date) => {
    return {
      day: date.format('ddd'),
      date: date.format('D'),
      month: date.format('MMM')
    };
  };
  
  // Check if a date is selected
  const isDateSelected = (date) => {
    return date.format('YYYYMMDD') === selectedDate.format('YYYYMMDD');
  };
  
  // Get performance percent for a specific date and stop type
  const getPerformancePercent = (date, type) => {
    const dateKey = date.format('YYYYMMDD');
    const dateData = datePerformance[dateKey];
    
    if (!dateData) return 0;
    
    switch (type) {
      case 'overall':
        return calculateOverallPerformance(date).percent;
      case 'first':
        return dateData.firstStops.percent;
      case 'mid':
        // Return null (N/A) if there are no midpoints for this route
        return dateData.midStops.hasMidpoints ? dateData.midStops.percent : null;
      case 'last':
        return dateData.lastStops.percent;
      default:
        return 0;
    }
  };
  
  // Get record count for a specific date and stop type
  const getRecordCount = (date, type) => {
    const dateKey = date.format('YYYYMMDD');
    const dateData = datePerformance[dateKey];
    
    if (!dateData) return 0;
    
    switch (type) {
      case 'overall':
        return calculateOverallPerformance(date).total;
      case 'first':
        return dateData.firstStops.total;
      case 'mid':
        return dateData.midStops.total;
      case 'last':
        return dateData.lastStops.total;
      default:
        return 0;
    }
  };

  // Get label for stop type
  const getStopTypeLabel = (type) => {
    switch (type) {
      case 'overall':
        return 'Overall';
      case 'first':
        return 'First Stops';
      case 'mid':
        return 'Mid Stops';
      case 'last':
        return 'Last Stops';
      default:
        return '';
    }
  };
  
  // Get today's date for highlighting
  const today = dayjs().format('YYYYMMDD');

  // Get Today's button handler
  const handleTodayClick = () => {
    const todayDate = dayjs();
    setSelectedDate(todayDate);
    setCurrentMonth(todayDate);
  };

  // Generate OTR issues and recommendations
  const generateOTRIssuesAndRecommendations = () => {
    // Only generate insights if we have enough data
    if (Object.keys(datePerformance).length < 3) {
      return [];
    }

    const issuesAndRecommendations = [];
    const dateKeys = Object.keys(datePerformance).sort();
    
    // Filter for days with enough data
    const validDates = dateKeys.filter(date => 
      datePerformance[date].total > 10
    );
    
    if (validDates.length < 3) return [];
    
    // Check for patterns in specific stop types by route
    const routePerformance = {};
    
    // Group data by route
    validDates.forEach(date => {
      const dateData = datePerformance[date];
      if (dateData && dateData.routeData) {
        Object.keys(dateData.routeData).forEach(routeId => {
          if (!routePerformance[routeId]) {
            routePerformance[routeId] = {
              overall: { total: 0, onTime: 0 },
              firstStops: { total: 0, onTime: 0 },
              midStops: { total: 0, onTime: 0 },
              lastStops: { total: 0, onTime: 0 },
              dates: []
            };
          }
          
          const routeData = dateData.routeData[routeId];
          routePerformance[routeId].overall.total += routeData.total || 0;
          routePerformance[routeId].overall.onTime += routeData.onTime || 0;
          routePerformance[routeId].firstStops.total += routeData.firstStops?.total || 0;
          routePerformance[routeId].firstStops.onTime += routeData.firstStops?.onTime || 0;
          routePerformance[routeId].midStops.total += routeData.midStops?.total || 0;
          routePerformance[routeId].midStops.onTime += routeData.midStops?.onTime || 0;
          routePerformance[routeId].lastStops.total += routeData.lastStops?.total || 0;
          routePerformance[routeId].lastStops.onTime += routeData.lastStops?.onTime || 0;
          
          if (!routePerformance[routeId].dates.includes(date)) {
            routePerformance[routeId].dates.push(date);
          }
        });
      }
    });
    
    // For demo purposes, create sample route data if no real data exists
    if (Object.keys(routePerformance).length === 0) {
      // Create sample data for routes 900-913
      for (let i = 0; i < 5; i++) {
        const routeId = `90${i}`;
        routePerformance[routeId] = {
          overall: { total: 100, onTime: 20 + Math.floor(Math.random() * 20) },
          firstStops: { total: 30, onTime: 10 + Math.floor(Math.random() * 10) },
          midStops: { total: 40, onTime: 5 + Math.floor(Math.random() * 15) },
          lastStops: { total: 30, onTime: 5 + Math.floor(Math.random() * 10) },
          dates: validDates.slice(0, 3 + Math.floor(Math.random() * 5))
        };
      }
    }
    
    // Calculate percentages and identify issues
    Object.keys(routePerformance).forEach((routeId, index) => {
      const data = routePerformance[routeId];
      
      // Calculate percentages
      const overallPercent = data.overall.total > 0 ? 
        Math.round((data.overall.onTime / data.overall.total) * 100) : 0;
        
      const firstStopsPercent = data.firstStops.total > 0 ? 
        Math.round((data.firstStops.onTime / data.firstStops.total) * 100) : 0;
      
      // Check if route has midpoints data before calculating percentage
      const hasMidpoints = data.midStops.total > 0;
      const midStopsPercent = hasMidpoints ? 
        Math.round((data.midStops.onTime / data.midStops.total) * 100) : null;
        
      const lastStopsPercent = data.lastStops.total > 0 ? 
        Math.round((data.lastStops.onTime / data.lastStops.total) * 100) : 0;
      
      // Skip routes with good performance
      if (overallPercent > 85) return;
      
      // Determine issue severity (for coloring)
      let severity = 'info'; // blue
      if (overallPercent < 50) {
        severity = 'error'; // red
      } else if (overallPercent < 70) {
        severity = 'warning'; // yellow/orange
      }
      
      // Identify specific issue and recommendation
      let issue = `Route ${routeId} has low on-time performance (${overallPercent}%)`;
      let recommendation = `Route ${routeId} needs schedule adjustment`;
      
      // More specific recommendations based on where the issues occur
      // Only consider mid-stops if the route has midpoints
      if (firstStopsPercent < 60 && (!hasMidpoints || midStopsPercent > 70) && lastStopsPercent > 70) {
        recommendation = `Route ${routeId} needs improved departure procedures and driver pre-trip training`;
      } 
      else if (firstStopsPercent > 70 && hasMidpoints && midStopsPercent < 60 && lastStopsPercent < 60) {
        recommendation = `Route ${routeId} needs mid-route timing adjustments to address bottlenecks`;
      }
      else if (firstStopsPercent > 70 && (!hasMidpoints || midStopsPercent > 70) && lastStopsPercent < 60) {
        recommendation = `Route ${routeId} needs increased runtime allocation for final segment`;
      }
      else if (firstStopsPercent < 60 && (hasMidpoints ? midStopsPercent < 60 : true) && lastStopsPercent < 60) {
        recommendation = `Route ${routeId} requires comprehensive schedule review - all segments underperforming`;
      }
      
      // Add to issues list (limit to top 5)
      if (index < 5) {
        issuesAndRecommendations.push({
          id: index + 1,
          routeId,
          issue,
          recommendation,
          severity,
          overallPercent,
          firstStopsPercent,
          midStopsPercent,
          lastStopsPercent,
          hasMidpoints
        });
      }
    });
    
    return issuesAndRecommendations;
  };
  
  const otrIssuesAndRecommendations = generateOTRIssuesAndRecommendations();

  // Helper function to get color based on percent value
  const getColorByPercent = (percent) => {
    return percent >= 95 ? '#52c41a' : '#f5222d';
  };

  // Calculate radiusForType - new function to determine radius for selected types
  const calculateRadius = (stopType, date) => {
    // When "All Stops" is selected, show a single large ring
    if (selectedStopTypes.includes('ALL')) {
      return { strokeWidth: 12, radius: 80 };
    }
    
    const hasMid = getPerformancePercent(date, 'mid') !== null;
    
    // Determine the number of active rings that will be displayed
    const activeCounts = selectedStopTypes.filter(type => {
      if (type === 'mid') return hasMid;
      return true;
    }).length;
    
    // Return the appropriate radius based on number of selected types
    if (activeCounts === 1) {
      return { strokeWidth: 12, radius: 80 };
    }
    
    // With 2-3 types, assign appropriate widths
    if (stopType === 'first') {
      return { strokeWidth: 10, radius: 80 };
    } else if (stopType === 'mid') {
      return { strokeWidth: 10, radius: 60 };
    } else if (stopType === 'last') {
      return { strokeWidth: 10, radius: activeCounts === 3 ? 40 : 60 };
    }
    
    return { strokeWidth: 10, radius: 80 };
  };

  // Add mapping between uppercase OTRDashboard.js stop types and lowercase component types
  const mapStopTypeFromRawData = (stopType) => {
    if (!stopType) return '';
    
    switch (stopType.toUpperCase()) {
      case 'F': return 'first';
      case 'M': return 'mid';
      case 'L': return 'last';
      case 'ALL': return 'ALL';
      default: return stopType.toLowerCase(); // Fallback
    }
  };

  const mapStopTypeToRawData = (stopType) => {
    if (!stopType) return '';
    
    switch (stopType.toLowerCase()) {
      case 'first': return 'F';
      case 'mid': return 'M';
      case 'last': return 'L';
      case 'all': return 'ALL';
      default: return stopType.toUpperCase(); // Fallback
    }
  };

  // Initialize with default state
  useEffect(() => {
    // Default to showing "ALL" stops to match OTRDashboard
    setSelectedStopTypes(['ALL']);
  }, []);

  return (
    <Card
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={4} style={{ margin: 0 }}>{title}</Title>
          <Space>
            <Select 
              value={selectedRoute} 
              onChange={handleRouteChange}
              style={{ width: 120 }}
            >
              <Option value="all">All Routes</Option>
              {availableRoutes.map(route => (
                <Option key={route} value={String(route)}>Route {route}</Option>
              ))}
            </Select>
          </Space>
        </div>
      }
      bordered={false}
      extra={
        <Button type="link" icon={<InfoCircleOutlined />} />
      }
    >
      {/* Month navigation */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Button 
          type="text" 
          icon={<LeftOutlined />} 
          onClick={handlePrevMonth} 
        />
        <Text strong style={{ fontSize: 16 }}>{currentMonth.format('MMMM YYYY')} <Button type="link" size="small" onClick={handleTodayClick}>Today</Button></Text>
        <Button 
          type="text" 
          icon={<RightOutlined />} 
          onClick={handleNextMonth} 
        />
      </div>
      
      {/* Date carousel with multi-radial indicators */}
      <div style={{ overflowX: 'auto', whiteSpace: 'nowrap', padding: '8px 0 24px 0' }}>
        <div style={{ display: 'inline-flex' }}>
          {visibleDates.map((date, index) => {
            const dateKey = date.format('YYYYMMDD');
            const isToday = dateKey === today;
            const isSelected = isDateSelected(date);
            const dateDisplay = formatDateDisplay(date);
            
            // Calculate overall performance based on selected stop types
            const overallPerformance = calculateOverallPerformance(date);
            const overallPercent = overallPerformance.percent;
            const hasData = overallPerformance.total > 0;
            
            return (
              <div 
                key={index}
                onClick={() => handleDateSelect(date)}
                style={{ 
                  padding: '0 8px',
                  cursor: 'pointer',
                  textAlign: 'center',
                  position: 'relative'
                }}
              >
                <div 
                  style={{ 
                    border: isSelected ? '2px solid #1890ff' : 'none',
                    borderRadius: '8px',
                    padding: '8px 4px',
                    backgroundColor: isSelected ? 'rgba(24, 144, 255, 0.1)' : isToday ? 'rgba(240, 242, 245, 0.5)' : 'transparent'
                  }}
                >
                  <div style={{ color: isSelected ? '#1890ff' : '#666', fontSize: 12, marginBottom: 4 }}>
                    {dateDisplay.day}
                  </div>
                  
                  {/* Multi-radial progress circles */}
                  <div style={{ margin: '8px 0', height: 80, width: 80, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {hasData ? (
                      <>
                        {/* When 'ALL' is selected, show all stop types */}
                        {(selectedStopTypes.includes('ALL') || selectedStopTypes.includes('first')) && (
                          <div 
                            key="first"
                            style={{ 
                              position: 'absolute', 
                              top: (80 - calculateRadius('first', date).radius) / 2, 
                              left: (80 - calculateRadius('first', date).radius) / 2, 
                              width: calculateRadius('first', date).radius, 
                              height: calculateRadius('first', date).radius 
                            }}
                          >
                            <Progress
                              type="circle"
                              percent={selectedStopTypes.includes('ALL') ? 
                                getPerformancePercent(date, 'overall') : 
                                getPerformancePercent(date, 'first')}
                              format={() => ''}
                              strokeColor={getColorByPercent(selectedStopTypes.includes('ALL') ? 
                                getPerformancePercent(date, 'overall') : 
                                getPerformancePercent(date, 'first'))}
                              trailColor="rgba(0,0,0,0.03)"
                              strokeWidth={calculateRadius('first', date).strokeWidth}
                              width={calculateRadius('first', date).radius}
                              style={{ opacity: 1 }}
                            />
                          </div>
                        )}
                        
                        {/* Skip mid stops if the route doesn't have them */}
                        {!selectedStopTypes.includes('ALL') && selectedStopTypes.includes('mid') && 
                          getPerformancePercent(date, 'mid') !== null && (
                          <div 
                            key="mid"
                            style={{ 
                              position: 'absolute', 
                              top: (80 - calculateRadius('mid', date).radius) / 2, 
                              left: (80 - calculateRadius('mid', date).radius) / 2, 
                              width: calculateRadius('mid', date).radius, 
                              height: calculateRadius('mid', date).radius 
                            }}
                          >
                            <Progress
                              type="circle"
                              percent={getPerformancePercent(date, 'mid')}
                              format={() => ''}
                              strokeColor={getColorByPercent(getPerformancePercent(date, 'mid'))}
                              trailColor="rgba(0,0,0,0.03)"
                              strokeWidth={calculateRadius('mid', date).strokeWidth}
                              width={calculateRadius('mid', date).radius}
                              style={{ opacity: 1 }}
                            />
                          </div>
                        )}
                        
                        {!selectedStopTypes.includes('ALL') && selectedStopTypes.includes('last') && (
                          <div 
                            key="last"
                            style={{ 
                              position: 'absolute', 
                              top: (80 - calculateRadius('last', date).radius) / 2, 
                              left: (80 - calculateRadius('last', date).radius) / 2, 
                              width: calculateRadius('last', date).radius, 
                              height: calculateRadius('last', date).radius 
                            }}
                          >
                            <Progress
                              type="circle"
                              percent={getPerformancePercent(date, 'last')}
                              format={() => ''}
                              strokeColor={getColorByPercent(getPerformancePercent(date, 'last'))}
                              trailColor="rgba(0,0,0,0.03)"
                              strokeWidth={calculateRadius('last', date).strokeWidth}
                              width={calculateRadius('last', date).radius}
                              style={{ opacity: 1 }}
                            />
                          </div>
                        )}
                        
                        {/* Overall percentage in the center */}
                        <div style={{ 
                          position: 'absolute', 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          fontSize: 16, 
                          fontWeight: 'bold', 
                          color: '#000',
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          background: '#fff',
                          boxShadow: 'inset 0 0 3px rgba(0,0,0,0.1)'
                        }}>
                          {overallPercent}%
                        </div>
                      </>
                    ) : (
                      <div style={{ 
                        width: 80, 
                        height: 80, 
                        borderRadius: '50%', 
                        border: '1px dashed #d9d9d9',
                        margin: '0 auto',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <Text type="secondary" style={{ fontSize: 10 }}>No data</Text>
                      </div>
                    )}
                  </div>
                  
                  <div style={{ 
                    fontSize: 14, 
                    fontWeight: isSelected || isToday ? 'bold' : 'normal',
                    color: isSelected ? '#1890ff' : isToday ? '#1890ff' : '#000'
                  }}>
                    {dateDisplay.date}
                  </div>
                  
                  <div style={{ fontSize: 12, color: '#999' }}>
                    {dateDisplay.month}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Stop type toggle pills */}
      <div style={{ marginBottom: 20, textAlign: 'center' }}>
        <Space size="small">
          <Tag.CheckableTag
            checked={selectedStopTypes.includes('ALL')}
            onChange={checked => {
              if (checked) {
                // When selecting "ALL", remove all other selections and add uppercase 'ALL'
                // to be consistent with OTRDashboard's expected format
                setSelectedStopTypes(['ALL']);
              } else {
                // If unchecking "ALL", default to "First Stops"
                setSelectedStopTypes(['first']);
              }
            }}
            style={{ 
              borderRadius: '16px', 
              padding: '2px 10px',
              backgroundColor: selectedStopTypes.includes('ALL') ? '#722ed1' : undefined,
              color: selectedStopTypes.includes('ALL') ? 'white' : undefined
            }}
          >
            All Stops
          </Tag.CheckableTag>
          
          <Tag.CheckableTag
            checked={selectedStopTypes.includes('first')}
            onChange={checked => {
              // Handle for first stops tag
              let newSelection = [...selectedStopTypes];
              // Remove "ALL" if it's selected
              newSelection = newSelection.filter(t => t !== 'ALL');
              
              if (checked) {
                if (!newSelection.includes('first')) {
                  newSelection.push('first');
                }
              } else {
                newSelection = newSelection.filter(t => t !== 'first');
              }
              
              // Ensure at least one option is selected
              setSelectedStopTypes(newSelection.length ? newSelection : ['first']);
            }}
            style={{ 
              borderRadius: '16px', 
              padding: '2px 10px',
              backgroundColor: selectedStopTypes.includes('first') ? '#722ed1' : undefined,
              color: selectedStopTypes.includes('first') ? 'white' : undefined
            }}
          >
            First Stops
          </Tag.CheckableTag>
          
          <Tag.CheckableTag
            checked={selectedStopTypes.includes('mid')}
            onChange={checked => {
              // Handle for mid stops tag
              let newSelection = [...selectedStopTypes];
              // Remove "ALL" if it's selected
              newSelection = newSelection.filter(t => t !== 'ALL');
              
              if (checked) {
                if (!newSelection.includes('mid')) {
                  newSelection.push('mid');
                }
              } else {
                newSelection = newSelection.filter(t => t !== 'mid');
              }
              
              // Ensure at least one option is selected
              setSelectedStopTypes(newSelection.length ? newSelection : ['first']);
            }}
            style={{ 
              borderRadius: '16px', 
              padding: '2px 10px',
              backgroundColor: selectedStopTypes.includes('mid') ? '#722ed1' : undefined,
              color: selectedStopTypes.includes('mid') ? 'white' : undefined
            }}
          >
            Mid Stops
          </Tag.CheckableTag>
          
          <Tag.CheckableTag
            checked={selectedStopTypes.includes('last')}
            onChange={checked => {
              // Handle for last stops tag
              let newSelection = [...selectedStopTypes];
              // Remove "ALL" if it's selected
              newSelection = newSelection.filter(t => t !== 'ALL');
              
              if (checked) {
                if (!newSelection.includes('last')) {
                  newSelection.push('last');
                }
              } else {
                newSelection = newSelection.filter(t => t !== 'last');
              }
              
              // Ensure at least one option is selected
              setSelectedStopTypes(newSelection.length ? newSelection : ['first']);
            }}
            style={{ 
              borderRadius: '16px', 
              padding: '2px 10px',
              backgroundColor: selectedStopTypes.includes('last') ? '#722ed1' : undefined,
              color: selectedStopTypes.includes('last') ? 'white' : undefined
            }}
          >
            Last Stops
          </Tag.CheckableTag>
        </Space>
      </div>
      
      {/* Selected date details */}
      {isDateSelected(selectedDate) && (
        <Card 
          style={{ marginTop: 16 }}
          size="small"
          title={
            <Text style={{ fontSize: 16 }}>
              {selectedDate.format('dddd, MMMM D, YYYY')} Performance Details
            </Text>
          }
        >
          <Row gutter={24}>
            <Col xs={24} sm={12} md={6}>
              <Statistic 
                title="Overall" 
                value={calculateOverallPerformance(selectedDate).percent}
                suffix="%"
                valueStyle={{ color: getColorByPercent(calculateOverallPerformance(selectedDate).percent) }}
              />
              <Text type="secondary">{calculateOverallPerformance(selectedDate).total} records</Text>
            </Col>
            {!selectedStopTypes.includes('ALL') && (
              <>
                <Col xs={24} sm={12} md={6}>
                  <Statistic 
                    title="First Stops" 
                    value={getPerformancePercent(selectedDate, 'first')}
                    suffix="%"
                    valueStyle={{ color: getColorByPercent(getPerformancePercent(selectedDate, 'first')) }}
                  />
                  <Text type="secondary">{getRecordCount(selectedDate, 'first')} records</Text>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  {getPerformancePercent(selectedDate, 'mid') !== null ? (
                    <>
                      <Statistic 
                        title="Mid Stops" 
                        value={getPerformancePercent(selectedDate, 'mid')}
                        suffix="%"
                        valueStyle={{ color: getColorByPercent(getPerformancePercent(selectedDate, 'mid')) }}
                      />
                      <Text type="secondary">{getRecordCount(selectedDate, 'mid')} records</Text>
                    </>
                  ) : (
                    <>
                      <Text strong style={{ fontSize: 16 }}>Mid Stops</Text>
                      <div>
                        <Text type="secondary">Not measured for this route</Text>
                      </div>
                    </>
                  )}
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Statistic 
                    title="Last Stops" 
                    value={getPerformancePercent(selectedDate, 'last')}
                    suffix="%"
                    valueStyle={{ color: getColorByPercent(getPerformancePercent(selectedDate, 'last')) }}
                  />
                  <Text type="secondary">{getRecordCount(selectedDate, 'last')} records</Text>
                </Col>
              </>
            )}
          </Row>
        </Card>
      )}
      
      {/* Top OTR Issues & Recommendations */}
      {otrIssuesAndRecommendations.length > 0 && (
        <Card 
          style={{ marginTop: 24 }}
        >
          <Row gutter={24}>
            <Col span={12}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
                <WarningOutlined style={{ color: '#faad14', marginRight: 8 }} />
                <Text strong style={{ fontSize: 16, margin: 0 }}>Top OTR Issues</Text>
              </div>
              <List
                size="small"
                dataSource={otrIssuesAndRecommendations}
                renderItem={item => (
                  <List.Item>
                    <Space align="start">
                      <Badge 
                        status={item.severity} 
                        style={{ marginTop: 5 }}
                      />
                      <div>
                        <Text strong>Issue #{item.id}</Text>
                        <div>{item.issue}</div>
                      </div>
                    </Space>
                  </List.Item>
                )}
              />
            </Col>
            <Col span={12}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
                <BulbOutlined style={{ color: '#52c41a', marginRight: 8 }} />
                <Text strong style={{ fontSize: 16, margin: 0 }}>Suggested Actions</Text>
              </div>
              <List
                size="small"
                dataSource={otrIssuesAndRecommendations}
                renderItem={item => (
                  <List.Item>
                    <Space align="start">
                      <Badge 
                        count={item.id}
                        style={{ 
                          backgroundColor: item.severity === 'error' ? '#f5222d' : 
                                          item.severity === 'warning' ? '#faad14' : '#1890ff',
                          marginTop: 5
                        }}
                      />
                      <div>
                        <Text strong>Recommendation #{item.id}</Text>
                        <div>{item.recommendation}</div>
                      </div>
                    </Space>
                  </List.Item>
                )}
              />
            </Col>
          </Row>

          {otrIssuesAndRecommendations.length === 0 && (
            <Alert
              message="No performance issues detected"
              description="Current performance is meeting targets across routes."
              type="success"
              showIcon
            />
          )}
        </Card>
      )}
    </Card>
  );
};

export default OTRPerformanceRadialChart; 